import { useState, useEffect, useMemo, useRef } from 'react';
import PubSub from 'pubsub-js';
import getTaskController from '../realtime-task-controller';

/**
 * Custom hook for managing TaskManager state and real-time updates
 */
export default function useTaskManager() {
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [selectedTask, setSelectedTask] = useState(null);
    const selectedTaskRef = useRef(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [filters, setFilters] = useState({
        status: [],
        suspended: []
    });
    const [isInitialized, setIsInitialized] = useState(false);
    const [serverTimeOffset, setServerTimeOffset] = useState(0);

    const controller = useMemo(() => getTaskController(), []);

    // Refresh DataTable every 5 seconds to update uptime for running tasks
    useEffect(() => {
        if (!isInitialized) return;

        const interval = setInterval(() => {
            setRefreshKey(prev => prev + 1);
        }, 5000);

        return () => clearInterval(interval);
    }, [isInitialized]);

    // Initialize controller
    useEffect(() => {
        const initializeController = async () => {
            try {
                controller.initialize();

                // Subscribe to all tasks
                await controller.subscribeToAllTasks();

                // Sync server time
                const clientTime = Date.now();
                const response = await controller.getTaskList({ page: 1, limit: 1 });
                if (response && response.serverTime) {
                    const offset = response.serverTime - clientTime;
                    setServerTimeOffset(offset);
                }

                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to initialize task controller:', error);
                setConnectionStatus('disconnected');
                setIsConnected(false);
            }
        };

        initializeController();

        return () => {
            controller.cleanup();
        };
    }, [controller]);

    // Sync ref with selectedTask state
    useEffect(() => {
        selectedTaskRef.current = selectedTask;
    }, [selectedTask]);

    // PubSub subscription for task updates
    useEffect(() => {
        const handleTaskChanged = async (msg, taskId) => {
            // If we have a selected task and it's the one that changed, fetch it directly
            if (selectedTaskRef.current && selectedTaskRef.current.id === taskId) {
                try {
                    const updatedTask = await controller.getTask(taskId);
                    setSelectedTask(updatedTask);
                    selectedTaskRef.current = updatedTask;
                } catch (error) {
                    console.error('[TaskManager] Failed to fetch task:', error);
                }
            }

            // Trigger list refresh
            setRefreshKey(prev => prev + 1);
        };

        const handleConnectionChange = (msg, status) => {
            setConnectionStatus(status);
            setIsConnected(status === 'connected');
        };

        // Subscribe to PubSub events
        const tokens = [
            PubSub.subscribe('task:changed', handleTaskChanged),
            PubSub.subscribe('task:connection_change', handleConnectionChange)
        ];

        // Cleanup
        return () => {
            tokens.forEach(token => PubSub.unsubscribe(token));
        };
    }, [controller]);

    // Request fields for DataTable - builds the match query
    const requestFields = useMemo(() => {
        const match = {};

        // Add status filter
        if (filters.status.length > 0) {
            match.status = { $in: filters.status.map(s => s.value) };
        }

        // Add suspended filter
        if (filters.suspended.length > 0) {
            const suspendedValue = filters.suspended[0].value;
            if (suspendedValue === 'suspended') {
                match.suspended = true;
            } else if (suspendedValue === 'active') {
                match.suspended = { $ne: true };
            }
        }

        // Return as { match } so it spreads correctly in DataTable controller
        return { match };
    }, [filters]);

    // Trigger refresh when filters change (but only after initialization)
    useEffect(() => {
        if (isInitialized) {
            setRefreshKey(prev => prev + 1);
        }
    }, [filters, isInitialized]);

    return {
        isConnected,
        connectionStatus,
        selectedTask,
        setSelectedTask,
        selectedTaskRef,
        refreshKey,
        setRefreshKey,
        filters,
        setFilters,
        isInitialized,
        serverTimeOffset,
        controller,
        requestFields
    };
}
